import { Role, User, UserRoleNodeMapping, UserRoleNodeMappingPayload } from '@ekkogmbh/apisdk';
import { Add, Save } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CoordinateInput } from 'src/Common/Components/CoordinateInput';
import useRequest from 'src/Common/Hooks/useRequest';
import useEkko from 'src/Common/Providers/EkkoProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import RolePicker from 'src/RoleManagement/RolePicker';
import SectionDrawerContent from 'src/SettingsSection/Components/SectionDrawerContent';
import { useSettingsSection } from 'src/SettingsSection/Providers/SettingsSectionProvider';
import UserPicker from 'src/UserManagement/UserPicker';

const SectionHeader = ({ title }: { title: string }): React.JSX.Element => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: 'text.secondary',
      fontSize: 11,
    }}
  >
    {title}
  </Typography>
);

const defaultValidUntil = (): string => moment().add(1, 'd').format('YYYY-MM-DD');

const isBranch = (a: string, b: string) => b.startsWith(a) || a.startsWith(b);

interface Props {
  /** When set, the panel is for adding mappings to this user — User picker is locked. */
  user?: User;
  /** When set, the panel is for adding mappings to this role — Role picker is locked. */
  role?: Role;
  /** When set, the panel edits an existing mapping. */
  mapping?: UserRoleNodeMapping;
}
export const UserMappingDrawer = ({ user, role, mapping }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const { refresh } = usePagination();
  const { setDrawerOpen } = useSettingsSection();

  const [, startSaving] = useRequest();

  const [selectedUser, setSelectedUser] = useState<User | undefined>(mapping?.user ?? user);
  const [selectedRole, setSelectedRole] = useState<Role | undefined>(mapping?.role ?? role);
  const [coordinate, setCoordinate] = useState<string>(mapping?.node?.coordinate ?? '');
  const [validUntil, setValidUntil] = useState<string>(
    mapping?.validUntil ? moment(mapping.validUntil).format('YYYY-MM-DD') : defaultValidUntil(),
  );

  const isEditMode = mapping !== undefined;

  const savePayload: UserRoleNodeMappingPayload | undefined = useMemo(() => {
    if (selectedUser === undefined || selectedRole === undefined || coordinate === '') {
      return undefined;
    }

    if (selectedUser?.automation && validUntil === '') {
      return undefined;
    }

    return {
      user: selectedUser,
      role: selectedRole,
      coordinate,
      validUntil,
    };
  }, [selectedRole, selectedUser, coordinate, validUntil]);

  const reset = useCallback(() => {
    setSelectedUser(mapping?.user ?? user);
    setSelectedRole(mapping?.role ?? role);
    setCoordinate(mapping?.node?.coordinate ?? '');
    setValidUntil(mapping?.validUntil ? moment(mapping.validUntil).format('YYYY-MM-DD') : defaultValidUntil());
  }, [user, role, mapping]);

  // on mount
  useEffect(() => {
    reset();
  }, []);

  const isUserAndRoleOnBranch = useMemo(() => {
    if (selectedUser === undefined || selectedRole === undefined) {
      return false;
    }

    return isBranch(selectedUser.coordinate, selectedRole.coordinate);
  }, [selectedUser, selectedRole]);

  const isCoordinateOnBranch = useMemo(
    () =>
      coordinate === '' ||
      (isUserAndRoleOnBranch &&
        isBranch(coordinate, selectedRole?.coordinate ?? '') &&
        isBranch(coordinate, selectedUser?.coordinate ?? '')),
    [coordinate, isUserAndRoleOnBranch],
  );

  // Pre-fill coordinate from the role when the selected role's coordinate
  // doesn't already share a prefix with the current input. Mirrors legacy
  // UserMappingPanel behavior so existing flows feel the same.
  useEffect(() => {
    if (selectedRole === undefined) {
      return;
    }

    const isCoordinatePrefix = selectedRole.coordinate.startsWith(coordinate);
    const isRolePrefix = coordinate.startsWith(selectedRole.coordinate);
    if (coordinate !== '' && (isCoordinatePrefix || isRolePrefix)) {
      return;
    }

    setCoordinate(selectedRole.coordinate);
  }, [selectedRole]);

  const save = useCallback(() => {
    if (savePayload === undefined) {
      return;
    }

    startSaving(
      () => {
        if (mapping !== undefined) {
          return api.updateUserRoleNodeMapping({ ...mapping, ...savePayload });
        } else {
          return api.addUserRoleNodeMapping(savePayload);
        }
      },
      () => {
        refresh();
        setDrawerOpen(false);
      },
    );
  }, [savePayload, mapping]);

  const headerElement = useMemo(
    () => (
      <Stack spacing={0}>
        <Typography variant="h6" fontWeight={700}>
          {isEditMode ? 'Edit mapping' : 'Add mapping'}
        </Typography>
        {isEditMode && selectedUser && selectedRole && (
          <Typography variant="caption" color="text.secondary">
            {selectedUser.username} · {selectedRole.name}
          </Typography>
        )}
      </Stack>
    ),
    [],
  );

  return (
    <SectionDrawerContent
      header={headerElement}
      actions={{
        reset: { onClick: reset },
        confirm: {
          onClick: save,
          label: isEditMode ? 'Save' : 'Create',
          icon: isEditMode ? <Save /> : <Add />,
          disabled: !isCoordinateOnBranch,
        },
      }}
    >
      <Stack spacing={4}>
        {/* Subject */}
        <Stack spacing={1.5}>
          <SectionHeader title="Subject" />
          <UserPicker disabled={user !== undefined} user={selectedUser} onChange={(u) => setSelectedUser(u)} />
          <RolePicker
            disabled={role !== undefined}
            role={selectedRole}
            onChange={(r) => setSelectedRole(r as Role | undefined)}
          />
        </Stack>

        {/* Scope */}
        <Stack spacing={1.5}>
          <SectionHeader title="Scope" />
          <CoordinateInput value={coordinate} onChange={setCoordinate} trailingDelimiter={false} />
          {!isCoordinateOnBranch && (
            <Typography textAlign={'center'} sx={{ color: (theme) => theme.palette.warning.main }}>
              {'subject or scope coordinates diverge'}
            </Typography>
          )}
        </Stack>

        {/* Validity */}
        {selectedUser && !selectedUser.automation && (
          <Stack spacing={1.5}>
            <SectionHeader title="Validity" />
            <DatePicker
              label="Valid until"
              disablePast
              value={moment(validUntil)}
              onChange={(date: Moment | null) => {
                if (date !== null) {
                  setValidUntil(date.format('YYYY-MM-DD'));
                }
              }}
              minDate={moment().add(1, 'd')}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Stack>
        )}
      </Stack>
    </SectionDrawerContent>
  );
};
